import { h, proxyCustomElement, HTMLElement, Host } from '@stencil/core/internal/client';
import { c as create$3, F as FormController } from './form.js';
import { q as postalValidation, u as stateValidation, v as cityValidation, x as lineTwoValidation, y as lineOneValidation, n as identityNameValidation } from './schema-validations.js';
import { R as heading3, al as billingForm } from './parts.js';
import './details.js';
import { n as numberOnlyHandler } from './utils3.js';
import { S as StateOptions } from './state-options.js';
import { d as defineCustomElement$4 } from './custom-popper2.js';
import { d as defineCustomElement$3 } from './form-control-select2.js';
import { d as defineCustomElement$2 } from './form-control-text2.js';
import { d as defineCustomElement$1 } from './form-control-tooltip2.js';

const Header3 = (props) => {
    return h("h3", { class: `header-3 ${props === null || props === void 0 ? void 0 : props.class}`, part: heading3 }, props.text);
};

const billingFormSchema = (postalOnly) => {
    const fullBillingForm = create$3({
        name: identityNameValidation.required('Enter full name'),
        address_line1: lineOneValidation.required('Enter street address'),
        address_line2: lineTwoValidation.nullable(),
        address_city: cityValidation.required('Enter city'),
        address_state: stateValidation.required('Select state'),
        address_postal_code: postalValidation.required('Enter postal code')
    });
    const postalForm = create$3({
        name: identityNameValidation.nullable(),
        address_line1: lineOneValidation.nullable(),
        address_line2: lineTwoValidation.nullable(),
        address_city: cityValidation.nullable(),
        address_state: stateValidation.nullable(),
        address_postal_code: postalValidation.required('Enter postal code')
    });
    return postalOnly ? postalForm : fullBillingForm;
};

const BillingForm = /*@__PURE__*/ proxyCustomElement(class BillingForm extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.errors = {};
        this.inputHandler = (name, value) => {
            this.formController.setValues(Object.assign(Object.assign({}, this.formController.values.getValue()), { [name]: value }));
        };
    }
    componentWillLoad() {
        const postalOnly = this.isPostalOnlyMode;
        this.formController = new FormController(billingFormSchema(postalOnly));
    }
    componentDidLoad() {
        this.formController.values.subscribe(values => this.billingInfo = Object.assign({}, values));
        this.formController.errors.subscribe(errors => {
            this.errors = Object.assign({}, errors);
        });
    }
    get isPostalOnlyMode() {
        return this.paymentMethodType === 'card' && this.hideCardBillingForm;
    }
    get hideAllBillingFields() {
        return this.paymentMethodType === 'bankAccount' && this.hideBankAccountBillingForm;
    }
    async getValues() {
        return this.formController.values.getValue();
    }
    async fill(fields) {
        this.formController.setInitialValues(fields);
    }
    async validate() {
        let isValid = await this.formController.validate();
        return { isValid: isValid };
    }
    render() {
        const billingFormDefaultValue = this.formController.getInitialValues();
        const showHeader = !this.isPostalOnlyMode && !this.hideAllBillingFields;
        return (h(Host, { key: '0df4ecdee8c32874325b83dd79586c7449ce6d93' }, h("div", { key: '6d5b45f278a7c69a76fe0895036ab37b810d0262', part: billingForm, class: "mt-4", hidden: this.hideAllBillingFields }, showHeader && h(Header3, { key: 'bc8f57f42134859e28dd523500ef874101198076', text: "Billing address", class: "fs-6 fw-bold lh-lg mb-4" }), h("form", { key: '908b8265b028caaa01314bc56bdbfe550b632b94' }, h("fieldset", { key: 'af3e34fca100b0e0c3f13ef3af3a88009859e277' }, this.legend && h("legend", { key: '15cfcc98681d4386afad33d68910f7732e88b9a9' }, this.legend), h("div", { key: 'de372fbe06a6da1f512170a6f79f27c96499d790', class: "row gy-3" }, h("div", { key: 'bb4aab9d17a7bed7cb09e4306158f5e0ff248b8f', class: "col-12", hidden: this.isPostalOnlyMode }, h("form-control-text", { key: 'f8e086504b6f3fac0e4bd2a0c0dee5e2f2c3328a', name: 'name', label: 'Full Name', defaultValue: billingFormDefaultValue.name, errorText: this.errors.name, inputHandler: this.inputHandler })), h("div", { key: '9a5105e1183a61c55639c8153a65169fb65998c5', class: "col-12", hidden: this.isPostalOnlyMode }, h("form-control-text", { key: '4802e1462b0b1f433c77f817b733611332b18711', name: 'address_line1', label: 'Street Address', defaultValue: billingFormDefaultValue.address_line1, errorText: this.errors.address_line1, inputHandler: this.inputHandler })), h("div", { key: '3edf7b2390c2fa7d0cadd75f9102a63f88a3685b', class: "col-12", hidden: this.isPostalOnlyMode }, h("form-control-text", { key: 'bec348d7030fa44e5a627ebc3f67ed9689a4a5b9', name: 'address_line2', label: "Apartment, Suite, etc. (optional)", defaultValue: billingFormDefaultValue.address_line2, errorText: this.errors.address_line2, inputHandler: this.inputHandler })), h("div", { key: '066d141b9529aae210fc2585bd2fd386e5b82120', class: "col-12", hidden: this.isPostalOnlyMode }, h("form-control-text", { key: '602587fcca0666d7070e5cd0316ed1c541953337', name: 'address_city', label: "City", defaultValue: billingFormDefaultValue.address_city, errorText: this.errors.address_city, inputHandler: this.inputHandler })), h("div", { key: '125aeec26f292763abefe1c3a1a3070a2a8b4c7c', class: "col-12", hidden: this.isPostalOnlyMode }, h("form-control-select", { key: 'fb8d46d217d452809ee199ef8640e93714a91e85', name: 'address_state', label: 'State', options: StateOptions, defaultValue: billingFormDefaultValue.address_state, errorText: this.errors.address_state, inputHandler: this.inputHandler })), h("div", { key: 'b622a69cb724ed15d2fe0fb2a9039635bc96a12e', class: "col-12", hidden: this.hideAllBillingFields }, h("form-control-text", { key: 'a094770db85d5a65524909b5c4ec532fca167a95', name: 'address_postal_code', label: "ZIP", defaultValue: billingFormDefaultValue.address_postal_code, errorText: this.errors.address_postal_code, inputHandler: this.inputHandler, maxLength: 5, keyDownHandler: numberOnlyHandler }))))))));
    }
}, [0, "justifi-billing-form", {
        "legend": [1025],
        "hideCardBillingForm": [4, "hide-card-billing-form"],
        "hideBankAccountBillingForm": [4, "hide-bank-account-billing-form"],
        "paymentMethodType": [1, "payment-method-type"],
        "formController": [32],
        "billingInfo": [32],
        "errors": [32],
        "getValues": [64],
        "fill": [64],
        "validate": [64]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["justifi-billing-form", "custom-popper", "form-control-select", "form-control-text", "form-control-tooltip"];
    components.forEach(tagName => { switch (tagName) {
        case "justifi-billing-form":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BillingForm);
            }
            break;
        case "custom-popper":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "form-control-select":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "form-control-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "form-control-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { BillingForm as B, Header3 as H, defineCustomElement as d };
